
import React, { useState, useRef } from 'react';
import { SimulationConfig, SongSlice } from '@/types';
import { AUDIO } from '@/services/AudioService';
import { Music, Zap, Layers, Trash2 } from 'lucide-react';

interface VisualSlicerProps {
    config: SimulationConfig;
    setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const VisualSlicer: React.FC<VisualSlicerProps> = ({ config, setConfig }) => {
    const duration = config.songTotalDuration || 1;
    const slices = (config.songSliceSequence || [])
        .map(s => typeof s === 'number' ? { start: 0, duration: s } : s)
        .filter(s => s && typeof s.start === 'number' && typeof s.duration === 'number');

    const [dragging, setDragging] = useState<{ index: number, type: 'START' | 'END' | 'MOVE' } | null>(null);
    const [zoom, setZoom] = useState(1);
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleMouseDown = (e: React.MouseEvent, index: number, type: 'START' | 'END' | 'MOVE') => {
        e.stopPropagation();
        setDragging({ index, type });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!dragging) return;
        const rect = e.currentTarget.getBoundingClientRect();
        if (rect.height === 0) return;

        setConfig(p => {
            const currentSeq = (p.songSliceSequence || []).map(s => typeof s === 'number' ? { start: 0, duration: s } : s);
            const newSeq = [...currentSeq];
            
            if (!newSeq[dragging.index]) return p;

            const slice = { ...newSeq[dragging.index] };
            const containerRect = e.currentTarget.getBoundingClientRect();
            const relativeY = e.clientY - containerRect.top;
            const absoluteTime = (relativeY / containerRect.height) * duration;

            if (dragging.type === 'START') {
                const end = slice.start + slice.duration;
                slice.start = Math.max(0, Math.min(absoluteTime, end - 0.01));
                slice.duration = Math.max(0.01, end - slice.start);
            } else if (dragging.type === 'END') {
                slice.duration = Math.max(0.01, Math.min(duration - slice.start, absoluteTime - slice.start));
            } else if (dragging.type === 'MOVE') {
                slice.start = Math.max(0, Math.min(duration - slice.duration, absoluteTime - slice.duration / 2));
            }
            
            newSeq[dragging.index] = slice;
            return { ...p, songSliceSequence: newSeq };
        });
    };

    const handleMouseUp = () => {
        if (dragging) {
            AUDIO.playSliceAtIndex(dragging.index, config);
        }
        setDragging(null);
    };

    const handleAddSlice = (e: React.MouseEvent) => {
        if (dragging) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const time = (y / rect.height) * duration;
        
        setConfig(p => {
            const currentSeq = (p.songSliceSequence || []).map(s => typeof s === 'number' ? { start: 0, duration: s } : s);
            const newSlice = { start: time, duration: p.songSliceDuration || 0.15 };
            const newSeq = [...currentSeq, newSlice].sort((a,b) => a.start - b.start);
            return { ...p, songSliceSequence: newSeq };
        });
    };

    const autoSliceInterval = () => {
        const input = prompt("Enter slice interval in seconds (e.g. 0.5):", "0.5");
        if (input === null) return;
        const interval = parseFloat(input);
        
        if (isNaN(interval) || interval <= 0.05) {
            alert("Interval must be greater than 0.05s to prevent crashes.");
            return;
        }
        
        const newSlices: SongSlice[] = [];
        let count = 0;
        for (let t = 0; t < duration; t += interval) {
            newSlices.push({ start: t, duration: Math.min(interval, duration - t) });
            count++;
            if (count > 500) {
                alert("Too many slices generated (max 500). Stopping early to prevent crash.");
                break;
            }
        }
        setConfig(p => ({ ...p, songSliceSequence: newSlices }));
    };

    const autoSlicePeaks = () => {
        const peaks = AUDIO.getSongPeaks(0.2); 
        if (peaks.length === 0) {
            alert("No peaks detected. Try a different song or lower threshold.");
            return;
        }
        
        const newSlices: SongSlice[] = [];
        for (let i = 0; i < peaks.length; i++) {
            const start = peaks[i];
            const end = (i < peaks.length - 1) ? peaks[i+1] : duration;
            newSlices.push({ start, duration: Math.max(0.1, end - start) });
        }
        setConfig(p => ({ ...p, songSliceSequence: newSlices }));
    };

    return (
        <div className="flex flex-col gap-5">
            {/* Source Display */}
            {config.selectedSlicerSong && (
                <div className={`flex items-center justify-between px-5 py-4 rounded-2xl border animate-in fade-in slide-in-from-top-4 duration-500 ${config.darkMode ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-emerald-50 border-emerald-100'}`}>
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                            <Music className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <span className="text-[8px] font-display font-black uppercase tracking-[0.2em] opacity-40">Connected Signal</span>
                            <span className={`text-[11px] font-black uppercase tracking-widest truncate max-w-[140px] ${config.darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{config.selectedSlicerSong}</span>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                        <span className="text-[8px] font-display font-black uppercase tracking-[0.2em] opacity-40">Core Mass</span>
                        <span className={`text-[11px] font-mono font-bold ${config.darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{duration.toFixed(2)}s</span>
                    </div>
                </div>
            )}

            <div className="flex gap-2 items-center">
                <button 
                  onClick={autoSliceInterval} 
                  className={`flex-1 h-12 rounded-xl text-[9px] font-display font-black uppercase tracking-widest transition-all active:scale-95 border flex items-center justify-center gap-2 ${config.darkMode ? 'bg-blue-600/10 border-blue-500/20 hover:bg-blue-600/20 text-blue-400' : 'bg-blue-50 border-blue-100 hover:bg-blue-100 text-blue-600 shadow-sm'}`}
                >
                    <Layers className="w-3.5 h-3.5" />
                    Grid Slicing
                </button>
                <button 
                  onClick={autoSlicePeaks} 
                  className={`flex-1 h-12 rounded-xl text-[9px] font-display font-black uppercase tracking-widest transition-all active:scale-95 border flex items-center justify-center gap-2 ${config.darkMode ? 'bg-purple-600/10 border-purple-500/20 hover:bg-purple-600/20 text-purple-400' : 'bg-purple-50 border-purple-100 hover:bg-purple-100 text-purple-600 shadow-sm'}`}
                >
                    <Zap className="w-3.5 h-3.5" />
                    Peak Sync
                </button>
                <div className={`flex items-center gap-4 h-12 px-5 rounded-xl border ${config.darkMode ? 'bg-black/30 border-white/5' : 'bg-white border-zinc-950/5 shadow-inner'}`}>
                    <span className="text-[9px] font-display font-black uppercase tracking-widest opacity-30">Zoom</span>
                    <input 
                        type="range" min="1" max="10" step="1" 
                        value={zoom} onChange={e => setZoom(parseInt(e.target.value))}
                        className="w-16 h-1 rounded-full appearance-none cursor-pointer accent-emerald-500 bg-neutral-500/20"
                    />
                </div>
            </div>
            
            <div className={`relative w-full h-96 rounded-3xl border overflow-hidden transition-all duration-700 ${config.darkMode ? 'bg-black/60 border-white/5' : 'bg-zinc-50/50 border-zinc-950/5 shadow-inner'}`} ref={scrollRef}>
                <div 
                    className="relative w-full cursor-crosshair select-none"
                    style={{ height: `${100 * zoom}%` }}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onClick={handleAddSlice}
                >
                    {/* Grid Lines & Timestamps */}
                    <div className="absolute inset-0 flex flex-col justify-between py-4 opacity-20 pointer-events-none">
                        {Array.from({ length: Math.floor(16 * zoom) }).map((_, i) => (
                            <div key={i} className={`h-px ${config.darkMode ? 'bg-white/30' : 'bg-neutral-800/20'} w-full flex justify-end pr-4 text-[8px] font-mono tabular-nums`}>
                                {((i / (15 * zoom)) * duration).toFixed(2)}s
                            </div>
                        ))}
                    </div>

                    {/* Slices Overlay */}
                    {slices.map((slice, i) => {
                        const top = (slice.start / duration) * 100;
                        const height = (slice.duration / duration) * 100;
                        return (
                            <div 
                                key={i}
                                className="absolute left-2 right-10 bg-cyan-500/20 border-y border-x border-cyan-400/40 rounded-xl group hover:bg-cyan-400/30 transition-all cursor-grab active:cursor-grabbing shadow-xl shadow-cyan-900/10"
                                style={{ top: `${top}%`, height: `${height}%` }}
                                onMouseDown={(e) => handleMouseDown(e, i, 'MOVE')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    AUDIO.playSliceAtIndex(i, config);
                                }}
                            >
                                {/* Resize Handles */}
                                <div 
                                    className="absolute -top-1 left-0 right-0 h-3 cursor-ns-resize z-20 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onMouseDown={(e) => handleMouseDown(e, i, 'START')}
                                >
                                    <div className="w-12 h-1.5 bg-cyan-400 rounded-full mx-auto mt-0.5 shadow-lg" />
                                </div>
                                <div 
                                    className="absolute -bottom-1 left-0 right-0 h-3 cursor-ns-resize z-20 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onMouseDown={(e) => handleMouseDown(e, i, 'END')}
                                >
                                    <div className="w-12 h-1.5 bg-cyan-400 rounded-full mx-auto mb-0.5 shadow-lg" />
                                </div>

                                {/* Labeling */}
                                <div className="absolute top-2 left-3 flex flex-col gap-0.5">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-black text-white px-2 py-0.5 rounded-md bg-cyan-600/40 shadow-sm border border-white/10">SAMPLE {i+1}</span>
                                        <span className="text-[8px] font-black text-cyan-300/60 uppercase tracking-widest">{slice.duration.toFixed(3)}s</span>
                                    </div>
                                    <span className="text-[7px] font-mono text-cyan-300/30">OFFSET: {slice.start.toFixed(3)}s</span>
                                </div>

                                {/* Remove Button */}
                                <button 
                                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500/10 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all border border-red-500/20 active:scale-90 shadow-lg shadow-red-500/20"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setConfig(p => ({ ...p, songSliceSequence: (p.songSliceSequence || []).filter((_, idx) => idx !== i) }));
                                    }}
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </div>
                        );
                    })}
                    
                    {slices.length === 0 && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none gap-6">
                            <div className={`w-20 h-20 rounded-[2rem] border flex items-center justify-center animate-pulse rotate-12 transition-all ${config.darkMode ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/10'}`}>
                                <Music className={`w-8 h-8 ${config.darkMode ? 'text-white/20' : 'text-black/20'}`} />
                            </div>
                            <div className="flex flex-col items-center gap-2">
                                <p className={`text-[10px] font-display font-black uppercase text-center tracking-[0.3em] max-w-[16rem] leading-relaxed ${config.darkMode ? 'text-white/30' : 'text-black/30'}`}>
                                    Load Signal to Initialize<br/>Spatial Slicing Matrix
                                </p>
                                <div className="flex items-center gap-2 opacity-20">
                                    <div className="w-1 rounded-full h-1 bg-current" />
                                    <div className="w-1 rounded-full h-1 bg-current" />
                                    <div className="w-1 rounded-full h-1 bg-current" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
